import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lightbulb, Users, Newspaper, LineChart, Award, Sparkles, ArrowRight } from "lucide-react";

const IMG = {
  hero: "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/dncda7pf_Holiday%20Improvaneers-069.jpg",
  teach: "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/c7zzxfrr_Improvaneers%204%20Beyond%20Labels%202024.webp",
  stage: "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/fsofp8qx_Cleveland%202.webp",
};

const TIMELINE = [
  { icon: Lightbulb, year: "Early 2018", title: "A stolen idea", body: "Creator Rob Snow asked a friend what she'd do if she had the time. Her dream: to teach improv to people with special needs. \u201CImagine the possibilities!\u201D she said — and the wheels started turning." },
  { icon: Award, year: "1990s", title: "Trained with the best", body: "Rob studied improvisation at Second City, ImprovOlympic, and The Annoyance Theatre in Chicago — learning how improv builds problem-solving, quick thinking, teamwork, listening, confidence, and communication." },
  { icon: Users, year: "April 2018", title: "The Improvaneers are born", body: "After auditioning 20+ individuals, ten were chosen to become The Improvaneers — the world's first all–Down syndrome improvisation troupe, alongside co-founder Maggie Bisesi." },
  { icon: Sparkles, year: "July 2019", title: "Two sold-out shows", body: "\u201CNot Your Average News Team\u201D played to over 600 fans across two sold-out shows. From there, the world started to take notice." },
  { icon: Newspaper, year: "Late 2019", title: "Extra! Extra!", body: "Within two months: The Washington Post, TODAY, Inside Edition, The List, AOL, Yahoo News, Women's World, NPR and more. Time to write the handbook." },
  { icon: LineChart, year: "2020", title: "Proof in the pudding", body: "Working with behavioralist Dr. Anna Esbensen (Cincinnati Children's), an evaluation method measured skill growth over a year. The results — and parent stories — proved it out beyond expectations." },
];

const SKILL_STATS = [
  { n: "53%", l: "average skill growth" },
  { n: "70%", l: "Listening" },
  { n: "70%", l: "Problem solving" },
  { n: "68%", l: "Adapting to change" },
  { n: "46%", l: "Voice projection" },
  { n: "45%", l: "Eye contact" },
  { n: "42%", l: "Confidence" },
  { n: "100%", l: "liked or loved it" },
];

const PRESS = [
  { name: "The Washington Post", src: "https://images.squarespace-cdn.com/content/v1/5efe5eb3e2b42d3ef73d0dc7/1600822951020-Z354CFUJWY3LJJPNNS4K/washington-post-logo-thumb.jpg" },
  { name: "TODAY", src: "https://images.squarespace-cdn.com/content/v1/5efe5eb3e2b42d3ef73d0dc7/1600823088010-VNCIW7PBYFSH4TRQD59I/today-show.jpg" },
  { name: "Inside Edition", src: "https://images.squarespace-cdn.com/content/v1/5efe5eb3e2b42d3ef73d0dc7/1600823165996-ZOCW8T8GYVBIKK8KZPTH/inside-edition-logo.jpg" },
  { name: "PBS", src: "https://images.squarespace-cdn.com/content/v1/5efe5eb3e2b42d3ef73d0dc7/1600823216776-KDYCWEG61KF3HA5QZ4GK/wviz-pbs-logo.png" },
  { name: "The Mighty", src: "https://images.squarespace-cdn.com/content/v1/5efe5eb3e2b42d3ef73d0dc7/1600823265307-5T2EZ6P5Q6G40CBV7H02/the-mighty-logo.png" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow="About Us" title="Imagine the possibilities" image={IMG.hero}
        subtitle="How improvisation became a movement for confidence, connection, and opportunity — for people of ALL abilities." />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Mission */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Our mission</span>
            <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[#1a1617] leading-tight">
              If improv can build these skills, it can change lives.
            </h2>
            <p className="mt-4 text-[#545454] leading-relaxed">
              Improvisation teaches problem-solving, quick thinking, teamwork, listening, eye contact, and
              voice projection — while building real self-confidence. Taught the right way, those skills expand
              social, workplace, and lifetime opportunities, and shift how the world sees ability.
            </p>
            <div className="mt-6 rounded-2xl bg-[#ffdedf] border border-[#f3c6c8] p-5">
              <p className="font-display text-xl text-[#550001]">
                As of August 2024, our classes are open to folks of <strong>ALL abilities</strong> — fully inclusive. Improv is for ALL!
              </p>
            </div>
          </div>
          <img src={IMG.teach} alt="A director leads a joyful, inclusive improv class" loading="lazy"
            className="rounded-3xl border border-[#dfd8da] w-full object-cover aspect-[4/3]" />
        </div>

        {/* The Method Way */}
        <blockquote className="mt-16 rounded-3xl bg-[#221517] text-[#dfd8da] p-8 lg:p-10 border-l-4 border-[#bf6163]" data-testid="about-method-way">
          <p className="text-base sm:text-lg leading-relaxed italic">"The Improvaneer Method Way focuses on supporting our mission while maintaining immense respect, admiration, and support for the individuals utilizing the program. At all times, we create a positive learning atmosphere that incorporates fun, humor, and discipline. No one who participates will ever be forced to do something they don't want to do or aren't ready to do, and they will never be told to do something that scares or embarrasses them. We work to understand each individual's abilities and thresholds of discovery and enjoyment to provide the best experience possible."</p>
          <cite className="mt-4 block not-italic font-bold text-white">— Rob Snow, CEO &amp; Creator</cite>
        </blockquote>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="about-stats">
          {[
            { n: "600+", l: "fans at two sold-out debut shows" },
            { n: "500+", l: "trained each week (in person & online)" },
            { n: "3", l: "levels: Improviser, Improvaneer, Performer" },
          ].map((s) => (
            <div key={s.l} className="bg-[#550001] text-white rounded-2xl p-6 text-center">
              <div className="font-display text-4xl">{s.n}</div>
              <div className="mt-1 text-sm text-[#ffdedf]">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="mt-20">
          <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Our story</span>
          <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[#1a1617]">How The Improvaneer Method began</h2>
          <div className="mt-8 relative border-l-2 border-[#dfd8da] pl-6 space-y-8" data-testid="about-timeline">
            {TIMELINE.map((t, i) => (
              <div key={i} className="relative" data-testid={`timeline-${i}`}>
                <span className="absolute -left-[35px] top-0 w-8 h-8 rounded-full bg-[#920e12] text-white flex items-center justify-center">
                  <t.icon size={16} aria-hidden="true" />
                </span>
                <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#bf6163]">{t.year}</div>
                <h3 className="mt-0.5 text-xl font-bold text-[#1a1617]">{t.title}</h3>
                <p className="mt-1 text-[#545454] leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quote */}
        <figure className="mt-20 rounded-3xl overflow-hidden relative">
          <img src={IMG.stage} alt="The Improvaneers performing on stage" loading="lazy" className="w-full h-72 object-cover" />
          <figcaption className="absolute inset-0 bg-[#221517]/80 flex items-center justify-center p-8 text-center">
            <div>
              <p className="font-display text-2xl lg:text-3xl text-white leading-snug max-w-3xl">
                &ldquo;This started with me being told to &lsquo;imagine the possibilities&rsquo;, but I could never have imagined this!&rdquo;
              </p>
              <p className="mt-3 text-[#ffdedf] font-bold uppercase tracking-widest text-sm">— Rob Snow, Creator</p>
            </div>
          </figcaption>
        </figure>

        {/* Impact */}
        <div id="impact" className="mt-20 scroll-mt-24" data-testid="about-impact">
          <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Our impact</span>
          <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[#1a1617]">Proven, measurable growth</h2>
          <p className="mt-3 text-[#545454] max-w-2xl">In an independent evaluation of 20 participants with developmental disabilities, the Improvaneer Method produced growth across every skill measured — and families loved it.</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="impact-skill-stats">
            {SKILL_STATS.map((s) => (
              <div key={s.l} className="bg-[#550001] text-white rounded-2xl p-5 text-center">
                <div className="font-display text-3xl sm:text-4xl">{s.n}</div>
                <div className="mt-1 text-xs sm:text-sm text-[#ffdedf]">{s.l}</div>
              </div>
            ))}
          </div>
          <blockquote className="mt-10 border-l-4 border-[#bf6163] pl-5 py-2 italic text-[#550001]">
            "Emma was the maid of honor at her sister's wedding and had to give a speech. I was worried nobody would hear her — she's always been shy about public speaking. To my surprise she stood up with confidence and spoke loudly and clearly. Everyone has raved about her speech. I contribute it to her Improvaneer Method exercises. Thank you for giving our kids confidence while having fun!"
            <cite className="mt-3 block not-italic font-bold text-[#1a1617]">— Sharon, parent (OH)</cite>
          </blockquote>
          <div className="mt-10">
            <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12] mb-5">As seen on</p>
            <div className="flex flex-wrap items-center gap-8">
              {PRESS.map((p) => (
                <img key={p.name} src={p.src} alt={p.name} title={p.name} className="h-9 sm:h-11 w-auto object-contain opacity-80" />
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-3xl border border-[#dfd8da] p-8">
          <div>
            <h2 className="font-display text-2xl text-[#550001]">Ready to play?</h2>
            <p className="text-[#545454] mt-1">Join a fully inclusive class — improv really is for everyone.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Button asChild className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="about-register-btn"><Link to="/registration">Register for a class</Link></Button>
            <Button asChild variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="about-impact-btn"><a href="#impact">See our impact <ArrowRight size={16} className="ml-1" /></a></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
